import { Box, Stack, Typography } from '@mui/material'
import { FC, useCallback, useMemo, useState } from 'react'
import { RescheduleStep, useRescheduleOrderModule } from 'components/common/RescheduleOrder'
import { Trans, useTranslation } from 'react-i18next'

import { BKBNThemeWrapper } from '../BKBNThemeWrapper'
import { MUIButton } from 'components/common/MUIButton'
import { MUICheckbox } from 'components/common/MUICheckBox'
import Modal from 'components/common/Modals/Modal/Modal'
import { PhotographerContactDTO } from 'models/property'
import { QueryStatus } from 'components/common/QueryStatus'
import { RescheduleOrder } from 'components/common/RescheduleOrder/RescheduleOrder.module'
import { YELLOW_100 } from 'constants/styling/theme'
import { supportEmailHref } from 'constants/contacts'
import { useSnackbar } from 'components/contexts/SnackbarService.context'

interface Props {
  isOpen: boolean
  onClose: () => void
  /** Accepted photographer contact; when set, a "call your photographer first" notice is shown above the flow. */
  photographerContact?: PhotographerContactDTO | null
}

/**
 * Standalone modal containing Reschedule flow
 * @example <RescheduleOrderModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
 */
export const RescheduleOrderModal: FC<Props> = ({
  isOpen,
  onClose,
  photographerContact,
}) => {
  const { t } = useTranslation(['client_reschedule_modal', 'common', 'reschedule_order', 'embed'])
  const { spawnSuccessToast } = useSnackbar()
  const { isValid, setStep, reset, step, reschedule, rescheduleMutation, isPenaltyRequired } = useRescheduleOrderModule()

  const [penaltyAccepted, setPenaltyAccepted] = useState(false)
  const isModalValid = useMemo(() => isValid && (!isPenaltyRequired || penaltyAccepted), [isPenaltyRequired, isValid, penaltyAccepted])

  // A close while the request runs would drop its outcome (the mutation's callbacks die with an unmounted provider)
  const requestClose = useCallback(() => {
    if (rescheduleMutation.isPending) return
    onClose()
  }, [onClose, rescheduleMutation.isPending])

  return (
    <BKBNThemeWrapper>
      <Modal
        title={t('title')}
        isOpen={isOpen}
        onClose={requestClose}
        afterClosed={() => {
          reset()
          rescheduleMutation.reset()
          setPenaltyAccepted(false)
        }}
        footerContent={(
          <Stack gap={1} direction="row" justifyContent="flex-end" alignItems="center" maxWidth="600px">

            <Box flex="1 1 auto">

              {!isPenaltyRequired &&
                <Typography variant="text-xs">
                  <Trans t={t} i18nKey="conditions">
                    <a href={supportEmailHref()}>&nbsp;</a>
                  </Trans>
                </Typography>
              }

              {isPenaltyRequired &&
                <MUICheckbox
                  checked={penaltyAccepted}
                  onClick={() => setPenaltyAccepted((prev) => !prev)}
                  label={(
                    <Typography variant="text-xs">
                      <Trans t={t} i18nKey="conditions_penalty">
                        <a href={supportEmailHref()}>&nbsp;</a>
                      </Trans>
                    </Typography>
                  )}
                />
              }

            </Box>

            {step === RescheduleStep.FORM &&
              <>
                <MUIButton
                  sx={{ flex: '0 0 auto' }}
                  onClick={requestClose}
                  type="secondaryBorder"
                >
                  {t('actions:Back')}
                </MUIButton>

                <MUIButton
                  sx={{ flex: '0 0 auto' }}
                  disabled={!isModalValid}
                  onClick={() => setStep(RescheduleStep.CONFIRM)}
                >
                  {t('reschedule')}
                </MUIButton>

              </>
            }

            {step === RescheduleStep.CONFIRM &&
              <>

                <MUIButton
                  sx={{ flex: '0 0 auto' }}
                  onClick={() => setStep(RescheduleStep.FORM)}
                  type="secondaryBorder"
                >
                  {t('actions:Back')}
                </MUIButton>

                <MUIButton
                  sx={{ flex: '0 0 auto' }}
                  disabled={!isModalValid}
                  onClick={() => reschedule(() => {
                    spawnSuccessToast(t('reschedule_order:success_toast'))
                    onClose()
                  })}
                  isLoading={rescheduleMutation.isPending}
                >
                  {t('reschedule')}
                </MUIButton>

              </>
            }

          </Stack>
        )}
      >
        <Box width="90vw" maxWidth="600px">
          {!!photographerContact &&
            <Stack
              data-testid="reschedule-photographer-notice"
              direction="row"
              gap={1}
              alignItems="center"
              sx={{ bgcolor: YELLOW_100, borderRadius: 1, p: 1.5, mb: 2 }}
            >
              <Typography variant="text-sm">
                {t('embed:status_card.reschedule_notice', { name: photographerContact.name })}
                {!!photographerContact.phone && ` ${photographerContact.phone}`}
              </Typography>
            </Stack>
          }
          <RescheduleOrder />
          <QueryStatus
            query={rescheduleMutation}
            showStates={['error']}
            spaceTopRem={2}
            spaceBottomRem={2}
          />
        </Box>
      </Modal>
    </BKBNThemeWrapper>
  )
}
